const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const normalizeRadiusLocation = (item = {}) => {
  if (item.location && typeof item.location.lat === 'function' && typeof item.location.lng === 'function') {
    return {
      lat: item.location.lat(),
      lng: item.location.lng()
    };
  }

  if (item.location) {
    const lat = toFiniteNumber(item.location.lat);
    const lng = toFiniteNumber(item.location.lng);
    return lat === null || lng === null ? null : { lat, lng };
  }

  const lat = toFiniteNumber(item.lat);
  const lng = toFiniteNumber(item.lng);
  return lat === null || lng === null ? null : { lat, lng };
};

export const calculateDistanceKm = (lat1, lon1, lat2, lon2) => {
  const earthRadiusKm = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
};

export const calculateMemberRadiusResult = ({
  center,
  radiusKm,
  memberRadiusData,
  fallbackMarkers = []
}) => {
  const centerLocation = normalizeRadiusLocation(center);
  const radius = Number(radiusKm);
  if (!centerLocation || !Number.isFinite(radius) || radius <= 0) return null;

  const sourceLocations = Array.isArray(memberRadiusData?.locations) && memberRadiusData.locations.length > 0
    ? memberRadiusData.locations
    : fallbackMarkers.filter((marker) => marker.type === 'postal_cluster');

  let memberTotal = 0;
  let clusterCount = 0;
  const precisionBreakdown = {};
  const includedRows = [];

  sourceLocations.forEach((locationRecord) => {
    const location = normalizeRadiusLocation(locationRecord);
    if (!location) return;

    const distanceKm = calculateDistanceKm(
      centerLocation.lat,
      centerLocation.lng,
      location.lat,
      location.lng
    );

    if (distanceKm <= radius) {
      const value = Number(locationRecord.value) || 0;
      const precision = locationRecord.sourcePrecision || 'estimate';
      memberTotal += value;
      clusterCount += 1;
      precisionBreakdown[precision] = (precisionBreakdown[precision] || 0) + value;
      includedRows.push({
        id: locationRecord.id || `${locationRecord.name || 'member-location'}-${clusterCount}`,
        name: locationRecord.name || 'Unknown location',
        memberName: locationRecord.memberName || locationRecord.customerName || locationRecord.fullName || null,
        postalCode: locationRecord.postalCode || locationRecord.name || '',
        status: locationRecord.status || locationRecord.memberStatus || null,
        members: value,
        value,
        distanceKm,
        sourcePrecision: precision,
        basis: precision,
        confidence: precision === 'exact'
          ? 'Exact'
          : precision === 'postal_code_geocode'
            ? 'Postal code'
            : 'Estimated',
        location
      });
    }
  });

  const precisionKeys = Object.keys(precisionBreakdown);
  const basis = precisionKeys.length === 1 && precisionBreakdown.postal_code_geocode !== undefined
    ? 'postal-code geocode'
    : precisionKeys.length === 1 && precisionBreakdown.exact !== undefined
      ? 'exact coordinates'
      : precisionBreakdown.estimate !== undefined
        ? 'postal-area estimate'
        : 'member location data';

  return {
    members: memberTotal,
    clusters: clusterCount,
    excludedMissingLocation: Number(memberRadiusData?.excludedMissingLocation) || 0,
    totalCandidateMembers: Number(memberRadiusData?.totalMembers) || 0,
    mappableMembers: Number(memberRadiusData?.mappableMembers) || 0,
    basis,
    precisionBreakdown,
    rows: includedRows.sort((a, b) => a.distanceKm - b.distanceKm),
    rowMode: 'cluster'
  };
};
